import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Seo from "@/components/Seo";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { Eyebrow } from "@/components/shared";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (d) =>
  new Date(d + "T12:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const News = () => {
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/articles`)
      .then((r) => setArticles(r.data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = ["All", ...new Set(articles.map((a) => a.category))];
  const filtered = category === "All" ? articles : articles.filter((a) => a.category === category);
  const [featured, ...rest] = filtered;

  return (
    <>
      <Seo
        title="News & Editorial | DC, Maryland & Virginia Real Estate Insights — Scherif Real Estate"
        siteName="Scherif Real Estate"
        description="Editorial insight on Washington DC luxury real estate, Maryland and Northern Virginia markets, buying, selling, and vacation homes."
      />

      <section className="bg-stone-beige pb-16 pt-36 sm:pt-48" data-testid="news-header">
        <div className="container-luxe">
          <FadeUp>
            <Eyebrow className="mb-4">The Journal</Eyebrow>
            <h1 className="font-serif text-5xl font-light tracking-tight text-charcoal sm:text-6xl">News &amp; Editorial</h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-muted">
              Perspective on the region&apos;s luxury market — written for buyers, sellers, and the simply curious.
            </p>
          </FadeUp>
          <FadeUp delay={0.15} className="mt-10 flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`news-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                  category === c ? "border-charcoal bg-charcoal text-ivory" : "border-charcoal/20 text-slate-muted hover:border-champagne hover:text-charcoal"
                }`}
              >
                {c}
              </button>
            ))}
          </FadeUp>
        </div>
      </section>

      <section className="container-luxe py-16 sm:py-24" data-testid="news-list">
        {loading ? (
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-slate-muted" data-testid="news-loading">Loading editorial…</p>
        ) : filtered.length === 0 ? (
          <p className="font-serif text-2xl font-light text-slate-muted" data-testid="news-empty">New stories are on their way.</p>
        ) : (
          <>
            {featured && (
              <FadeUp>
                <Link to={`/news/${featured.slug}`} className="group grid gap-8 lg:grid-cols-12 lg:gap-14" data-testid={`article-card-${featured.slug}`}>
                  <div className="img-zoom lg:col-span-7">
                    <img src={featured.image} alt={featured.title} className="aspect-[16/10] w-full object-cover" loading="lazy" />
                  </div>
                  <div className="flex flex-col justify-center lg:col-span-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-champagne">
                      {featured.category} — {formatDate(featured.date)}
                    </p>
                    <h2 className="mt-4 font-serif text-3xl font-light leading-[1.15] tracking-tight text-charcoal transition-colors group-hover:text-champagne sm:text-4xl">
                      {featured.title}
                    </h2>
                    <p className="mt-5 text-sm leading-relaxed text-slate-muted">{featured.excerpt}</p>
                    <p className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-charcoal">
                      Read More <ArrowRight size={12} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-2" aria-hidden="true" />
                    </p>
                  </div>
                </Link>
              </FadeUp>
            )}
            <Stagger className="mt-20 grid gap-x-8 gap-y-16 md:grid-cols-2 lg:grid-cols-3">
              {rest.map((a) => (
                <StaggerItem key={a.slug}>
                  <Link to={`/news/${a.slug}`} className="group block" data-testid={`article-card-${a.slug}`}>
                    <div className="img-zoom">
                      <img src={a.image} alt={a.title} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                    </div>
                    <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-champagne">
                      {a.category} — {formatDate(a.date)}
                    </p>
                    <h3 className="mt-3 font-serif text-2xl font-light leading-snug text-charcoal transition-colors group-hover:text-champagne">{a.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-muted">{a.excerpt}</p>
                    <p className="mt-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-charcoal">
                      Read More <ArrowRight size={12} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-2" aria-hidden="true" />
                    </p>
                  </Link>
                </StaggerItem>
              ))}
            </Stagger>
          </>
        )}
      </section>
    </>
  );
};

export default News;
